import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import okReservaLogo from '../../assets/okreserva-logo.png';
import { cn } from '../../lib/utils';
import { useAppStore, useAuthStore } from '../../store/useStore';
import { Button } from '../ui/button';
import { LocationSelector } from './LocationSelector';
import { CurrencySelector } from '../CurrencySelector';
import {
  LayoutGrid, CalendarClock, Map, Users, BarChart3, Settings2, Code2,
  PanelLeftClose, PanelLeft, LogOut, Moon, Sun, Clock, CalendarDays,
  Megaphone, ShieldAlert, PartyPopper, TrendingDown, Sparkles, Plug,
  MessageCircle, Mail, Globe, Share2, Star, Crown, Gift, CreditCard,
  MessageSquare, FileText, Timer, AlertTriangle, ChevronDown, ChevronRight,
  Building2, BookOpen, Briefcase, TrendingUp, UserCog, Utensils, UtensilsCrossed,
  QrCode, Receipt, Shield, ShoppingCart, Wallet, Mic
} from 'lucide-react';

const NAV_GROUPS = [
  {
    key: 'operaciones',
    label: 'Operaciones',
    items: [
      { path: '/dashboard', icon: LayoutGrid, label: 'Dashboard' },
      { path: '/libro', icon: CalendarDays, label: 'Libro Digital' },
      { path: '/reservations', icon: CalendarClock, label: 'Reservaciones' },
      { path: '/waitlist', icon: Clock, label: 'Lista de Espera' },
      { path: '/walk-in', icon: QrCode, label: 'Fila Digital Walk-In' },
      { path: '/floor-plan', icon: Map, label: 'Plano de Sala' },
      { path: '/turn-time', icon: Timer, label: 'Rotación Mesas' },
    ]
  },
  {
    key: 'clientes',
    label: 'Clientes',
    items: [
      { path: '/customers', icon: Users, label: 'Clientes' },
      { path: '/loyalty', icon: Star, label: 'Fidelización' },
      { path: '/memberships', icon: Crown, label: 'Membresías' },
      { path: '/allergens', icon: AlertTriangle, label: 'Alérgenos' },
      { path: '/allergen-menu', icon: UtensilsCrossed, label: 'Menú Alérgenos' },
      { path: '/acceso-garantizado', icon: Shield, label: 'Acceso Garantizado' },
    ]
  },
  {
    key: 'carta',
    label: 'Carta & Menú',
    items: [
      { path: '/menus-cartas', icon: BookOpen, label: 'Menús & Cartas' },
      { path: '/widget-carta-express', icon: ShoppingCart, label: 'Widget Carta Express' },
    ]
  },
  {
    key: 'ingresos',
    label: 'Ingresos',
    items: [
      { path: '/events', icon: PartyPopper, label: 'Eventos' },
      { path: '/event-widget', icon: Code2, label: 'Widget Eventos' },
      { path: '/experiences', icon: Sparkles, label: 'Experiencias' },
      { path: '/experience-widget', icon: Code2, label: 'Widget Experiencias' },
      { path: '/gift-cards', icon: Gift, label: 'Tarjetas Regalo' },
      { path: '/gift-card-widget', icon: Code2, label: 'Widget Gift Cards' },
      { path: '/deposits', icon: CreditCard, label: 'Depósitos' },
      { path: '/private-dining', icon: Utensils, label: 'Comedores Privados' },
      { path: '/cfdi', icon: Receipt, label: 'Facturación CFDI' },
    ]
  },
  {
    key: 'okwallet',
    label: 'OkWallet',
    items: [
      { path: '/ok-wallet', icon: Wallet, label: 'Mi Cartera Digital' },
    ]
  },
  {
    key: 'canales',
    label: 'Canales',
    items: [
      { path: '/whatsapp', icon: MessageCircle, label: 'WhatsApp' },
      { path: '/followup', icon: Mail, label: 'Post-visita' },
      { path: '/booking-page', icon: Globe, label: 'Página de Reservas' },
      { path: '/social-bookings', icon: Share2, label: 'Reservas Sociales' },
    ]
  },
  {
    key: 'analiticas',
    label: 'Analíticas',
    items: [
      { path: '/analytics', icon: BarChart3, label: 'Analytics' },
      { path: '/nps', icon: MessageSquare, label: 'Feedback & NPS' },
      { path: '/reports', icon: FileText, label: 'Informes' },
      { path: '/benchmark', icon: TrendingUp, label: 'Benchmark Mercado' },
    ]
  },
  {
    key: 'empresarial',
    label: 'Empresarial',
    items: [
      { path: '/corporate', icon: Briefcase, label: 'Portal Corporativo' },
      { path: '/personal', icon: UserCog, label: 'Programación Personal' },
    ]
  },
  {
    key: 'marketing',
    label: 'Marketing',
    items: [
      { path: '/marketing', icon: Megaphone, label: 'Campañas' },
      { path: '/email-marketing', icon: Mail, label: 'Email Marketing' },
      { path: '/detection', icon: ShieldAlert, label: 'Alertas' },
      { path: '/risk-dashboard', icon: TrendingDown, label: 'No-Shows' },
    ]
  },
  {
    key: 'ia',
    label: 'Inteligencia IA',
    items: [
      { path: '/voice-agent', icon: Mic, label: 'Agente de Voz' },
    ]
  },
  {
    key: 'config',
    label: 'Configuración',
    items: [
      { path: '/mis-planes', icon: CreditCard, label: 'Mis Planes' },
      { path: '/integrations', icon: Plug, label: 'Integraciones' },
      { path: '/widget', icon: Code2, label: 'Widget' },
      { path: '/api-docs', icon: BookOpen, label: 'API Docs' },
      { path: '/settings', icon: Settings2, label: 'Ajustes' },
    ]
  },
];

export function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar, theme, toggleTheme } = useAppStore();
  const { logout, user } = useAuthStore();

  const defaultOpen = {};
  NAV_GROUPS.forEach(g => { defaultOpen[g.key] = true; });
  const [openGroups, setOpenGroups] = useState(defaultOpen);

  const toggleGroup = (key) => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const isActive = (path) => location.pathname === path;
  const isGroupActive = (items) => items.some(i => location.pathname === i.path);

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen flex flex-col border-r border-border bg-card transition-all duration-300",
        sidebarCollapsed ? "w-16" : "w-60"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className={cn(
        "flex h-16 items-center border-b border-border px-4 flex-shrink-0",
        sidebarCollapsed ? "justify-center" : "justify-between"
      )}>
        {!sidebarCollapsed && (
          <Link to="/dashboard" className="flex items-center gap-3">
            <img
              src={okReservaLogo}
              alt="OkReserva"
              className="h-8 w-auto"
              style={{ filter: theme === 'dark' ? 'brightness(0) invert(1)' : 'none' }}
            />
          </Link>
        )}
        {sidebarCollapsed && (
          <Link to="/dashboard">
            <img
              src={okReservaLogo}
              alt="OkReserva"
              className="h-8 w-8 object-contain"
              style={{ filter: theme === 'dark' ? 'brightness(0) invert(1)' : 'none' }}
            />
          </Link>
        )}
        {!sidebarCollapsed && (
          <Button variant="ghost" size="icon" onClick={toggleSidebar} className="h-8 w-8" data-testid="toggle-sidebar-btn">
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        )}
      </div>

      {sidebarCollapsed && (
        <div className="flex justify-center py-2 border-b border-border flex-shrink-0">
          <Button variant="ghost" size="icon" onClick={toggleSidebar} className="h-8 w-8" data-testid="toggle-sidebar-btn">
            <PanelLeft className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Location Selector */}
      {user?.has_multiple_locations && (
        <div className={cn("border-b border-border p-2 flex-shrink-0", sidebarCollapsed && "flex justify-center")}>
          <LocationSelector collapsed={sidebarCollapsed} />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 custom-scrollbar">
        {NAV_GROUPS.map((group) => {
          const groupActive = isGroupActive(group.items);
          const isOpen = openGroups[group.key];

          return (
            <div key={group.key} className="mb-1">
              {/* Group Header */}
              {!sidebarCollapsed && (
                <button
                  onClick={() => toggleGroup(group.key)}
                  className={cn(
                    "w-full flex items-center justify-between px-2 py-1.5 rounded-lg transition-colors",
                    "hover:bg-accent/50 group"
                  )}
                >
                  <span className={cn(
                    "text-[10px] font-bold uppercase tracking-wider transition-colors",
                    groupActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
                  )}>
                    {group.label}
                  </span>
                  {isOpen
                    ? <ChevronDown className="h-3 w-3 text-muted-foreground/60" />
                    : <ChevronRight className="h-3 w-3 text-muted-foreground/60" />}
                </button>
              )}

              {/* Group Items */}
              {(isOpen || sidebarCollapsed) && (
                <ul className={cn("space-y-0.5", !sidebarCollapsed && "mt-0.5 mb-1")}>
                  {group.items.map((item) => {
                    const active = isActive(item.path);
                    const Icon = item.icon;
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          title={sidebarCollapsed ? item.label : undefined}
                          className={cn(
                            "flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm font-medium transition-all duration-200",
                            active
                              ? "bg-primary text-primary-foreground shadow-sm shadow-primary/20"
                              : "text-muted-foreground hover:bg-accent hover:text-foreground",
                            sidebarCollapsed && "justify-center px-2"
                          )}
                          data-testid={`nav-${item.path.replace('/', '')}`}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Divider between groups (collapsed mode: just dots) */}
              {sidebarCollapsed && (
                <div className="my-1 mx-2 h-px bg-border/50" />
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className={cn(
        "border-t border-border p-3 flex-shrink-0",
        sidebarCollapsed ? "flex flex-col items-center gap-2" : "space-y-2"
      )}>
        <Button
          variant="ghost"
          size={sidebarCollapsed ? "icon" : "sm"}
          onClick={toggleTheme}
          className={cn("w-full rounded-xl", sidebarCollapsed && "w-10")}
          data-testid="theme-toggle-btn"
        >
          {theme === 'dark' ? (
            <><Sun className="h-4 w-4" />{!sidebarCollapsed && <span className="ml-2">Modo Claro</span>}</>
          ) : (
            <><Moon className="h-4 w-4" />{!sidebarCollapsed && <span className="ml-2">Modo Oscuro</span>}</>
          )}
        </Button>

        {!sidebarCollapsed && (
          <div className="px-2 py-1">
            <CurrencySelector />
          </div>
        )}
        {!sidebarCollapsed && user && (
          <div className="px-2 py-1 text-xs text-muted-foreground truncate">{user.email}</div>
        )}
        <Button
          variant="ghost"
          size={sidebarCollapsed ? "icon" : "sm"}
          onClick={logout}
          className={cn("w-full text-destructive hover:text-destructive rounded-xl", sidebarCollapsed && "w-10")}
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4" />
          {!sidebarCollapsed && <span className="ml-2">Cerrar sesión</span>}
        </Button>
      </div>
    </aside>
  );
}
